import React, { useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { PageLayout, PageHeader, SectionCard } from '../../components/ui';
import { getApiBase } from '../../config/env';

const API_BASE = getApiBase();

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();

  const initialEmail = sp.get('email') || '';
  const initialToken = sp.get('token') || '';

  const [identifier, setIdentifier] = useState(initialEmail);
  const [token, setToken] = useState(initialToken);
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');

  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [loading, setLoading] = useState(false);

  const mode = useMemo(() => {
    if (token) return 'token';
    if (otp) return 'otp';
    return 'token';
  }, [token, otp]);

  const onSubmit = async (e) => {
    e.preventDefault();

    const id = identifier.trim();
    if (!id) return toast.error('Email is required.');
    if (!password || password.length < 6) return toast.error('Password must be at least 6 characters.');
    if (password !== password2) return toast.error('Passwords do not match.');

    if (mode === 'token' && !token.trim()) return toast.error('Token is required.');
    if (mode === 'otp' && !/^\d{6}$/.test(otp.trim())) return toast.error('OTP must be 6 digits.');

    setLoading(true);
    try {
      const payload = {
        identifier: id,
        password,
        password_confirmation: password2,
      };
      if (mode === 'token') payload.token = token.trim();
      else payload.otp = otp.trim();

      const res = await fetch(`${API_BASE}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Reset failed');

      toast.success(data?.message || 'Password reset successful');
      navigate('/login');
    } catch (e2) {
      toast.error(e2?.message || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout maxWidth="sm" backgroundAccent>
      <PageHeader
        title="Reset Password"
        subtitle="Use the token from your email link (recommended) or OTP if enabled by HR."
        actions={
          <Button
            variant="outlined"
            startIcon={<ArrowBackOutlinedIcon />}
            onClick={() => navigate('/login')}
            disabled={loading}
          >
            Back to Login
          </Button>
        }
      />

      <SectionCard>
        <Alert severity="info" sx={{ mb: 2 }}>
          If you opened this page from email, your token and email should already be pre-filled.
        </Alert>

        <Box component="form" onSubmit={onSubmit} noValidate>
          <Stack spacing={2}>
            <TextField
              required
              label="Email"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              autoComplete="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Token (from email link)"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              helperText="Leave empty if you want to reset using OTP."
            />

            <TextField
              label="OTP (6 digits)"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              helperText="Use OTP only if HR sends OTP."
            />

            <TextField
              required
              label="New Password"
              type={show1 ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShow1((s) => !s)} edge="end" tabIndex={-1}>
                      {show1 ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              required
              label="Confirm Password"
              type={show2 ? 'text' : 'password'}
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              autoComplete="new-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShow2((s) => !s)} edge="end" tabIndex={-1}>
                      {show2 ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button type="submit" variant="contained" disabled={loading} sx={{ minHeight: 44 }}>
              {loading ? <CircularProgress size={17} color="inherit" /> : 'Reset Password'}
            </Button>

            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              Endpoint: {API_BASE}/auth/reset-password
            </Typography>
          </Stack>
        </Box>
      </SectionCard>
    </PageLayout>
  );
}

